import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import printJS from 'print-js';

import { useToast } from 'helpers/hooks';
import { File } from 'components/File';
import { FilePopup } from 'components/FilePopup'; 
import { Select } from 'components/Select';
import { Button } from 'components/Button';

import { ReactComponent as ChevronLeftIcon } from 'assets/svg/2_arrow_left.svg';
import { ReactComponent as ChevronRightIcon } from 'assets/svg/2_arrow_right.svg';
import { ReactComponent as PlusIcon } from 'assets/svg/2_zoomIn.svg';
import { ReactComponent as MinusIcon } from 'assets/svg/2_zoomOut.svg';
import { ReactComponent as UploadIcon } from 'assets/svg/2_upload.svg';
import { ReactComponent as ClockwiseIcon } from 'assets/svg/2_rotate_right.svg';
import { ReactComponent as CounterClockwiseIcon } from 'assets/svg/2_rotate_left.svg';
import { ReactComponent as DownloadIcon } from 'assets/svg/2_download.svg';
import { ReactComponent as PrintIcon } from 'assets/svg/2_print.svg';
import { ReactComponent as ShareIcon } from 'assets/svg/2_share.svg';

import styles from 'css_modules/FilePreview.module.css';

export const FilePreview = ({ doc, refresh, detailHeight, onShare }) => {
  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfWidth, setPdfWidth] = useState(null);
  const [viewOpen, setViewOpen] = useState(false); 

  const [rotateRightDeg, setRotateRightDeg] = useState(0);
  const [rotateLeftDeg, setRotateLeftDeg] = useState(0);

  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const showToast = useToast();

  const documentFileUrl = doc?.file_url;
  const isDocumentsLocation = location.pathname.startsWith('/documents');
  const uploadDisabled = isDocumentsLocation || !doc;

  const previous = pageNumber > 1 && numPages > 1;
  const next = pageNumber < numPages && numPages > 1;
  const zoomInEnabled = numPages && scale <= 2;
  const zoomOutEnabled = scale > 1;

  const getPreviousPage = () => previous && setPageNumber((p) => p - 1);
  const getNextPage = () => next && setPageNumber((p) => p + 1);
  const zoomIn = () => zoomInEnabled && setScale((s) => s + 0.25);
  const zoomOut = () => zoomOutEnabled && setScale((s) => s - 0.25);

  const rotateClockwise = () => {
    setRotation((r) => (r + 90) % 360);
    setRotateRightDeg((d) => d + 360);
  };

  const rotateCounterClockwise = () => {
    setRotation((r) => (r - 90 + 360) % 360);
    setRotateLeftDeg((d) => d - 360);
  };

  const handleView = () => file && setViewOpen(true);
  const handleShare = () => doc && onShare?.(doc);
  const handlePrint = () => file && printJS({ printable: file, type: 'pdf' });

  const handleDownload = async () => {
    if (!file) return;
    const res = await fetch(file);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = file.split('/').pop();
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleUpload = async (e) => {
    if (!doc) return;
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const body = new FormData();
    body.append('attachment', uploadedFile);
    const pathBase = doc.is_active ? 'my_documents' : 'archive';

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/${pathBase}/${doc.uuid}/add_attachment/`,
        {
          method: 'POST',
          headers: { Authorization: 'JWT ' + localStorage.getItem('DocspointJWT') },
          body,
        }
      );
      if (res.ok) {
        refresh();
        showToast('Příloha byla nahrána');
      } else showToast('Chyba při nahrávání souboru');
    } catch {
      showToast('Chyba při nahrávání souboru');
    }
  };

  const getSelectData = () => {
    if (!doc?.attachments) return [];
    const options = doc.attachments.map((a) => ({ id: a.url, title: a.filename }));
    if (doc.file_url) options.unshift({ id: doc.file_url, title: 'Soubor dokladu' });
    return options;
  };

  const myHeight = detailHeight ? '83vh' : '70vh';

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      try {
        setPdfWidth(containerRef.current.clientWidth * 0.97);
      } catch {}
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setFile(documentFileUrl);
    setNumPages(null);
    setPageNumber(documentFileUrl ? 1 : null);
    setScale(1);
    setRotation(0);
    setViewOpen(false); 
  }, [documentFileUrl]);

  useEffect(() => {
    if (viewOpen) {
      setViewOpen(false);
    }
  }, [viewOpen]);

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={`${styles.optionsRow} ${styles.topRow}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {getSelectData().length > 1 && (
            <Select
              data={getSelectData()}
              disabled={!doc?.attachments?.length}
              notNull={doc?.file_url}
              value={file || ''}
              onChange={(e) => {
                setPageNumber(1);
                setFile(e.target.value);
              }}
              className={styles.select}
            />
          )}
          {getSelectData().length === 1 && (
            <div style={{ margin: "0.5rem", marginLeft: "0.7rem", fontSize: "var(--s)", fontWeight: "500" }}>
              Soubor dokladu
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div className={styles.pagesContainer}>
            <ChevronLeftIcon
              title="Předchozí stránka"
              onClick={getPreviousPage}
              className={previous ? styles.chevronIconActive : styles.chevronIconInactive}
            />
            <span className={styles.pagesText}>
              {pageNumber || '–'}/{numPages || '–'}
            </span>
            <ChevronRightIcon
              title="Následující stránka"
              onClick={getNextPage}
              className={next ? styles.chevronIconActive : styles.chevronIconInactive}
            />
          </div>
          <Button className={styles.viewButton} title="Prohlédnout" onClick={handleView}>
            Náhled
          </Button>
        </div>
      </div>

      <div className={styles.placeholder} style={{ height: myHeight, overflow: 'auto', flexDirection: "column" }}>
        <File file={file} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
          {pageNumber && (
            <Page
              pageNumber={pageNumber}
              scale={scale}
              width={pdfWidth}
              rotate={rotation}
              className={styles.page}
            />
          )}
        </File>
      </div>

      <div className={`${styles.optionsRow} ${styles.bottomRow}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ClockwiseIcon
            title="Otáčet po směru hodinových ručiček"
            onClick={rotateClockwise}
            className={styles.rotateIcon}
            style={{ transform: `rotate(${rotateRightDeg}deg)`, transition: 'transform 0.2s' }}
          />
          <CounterClockwiseIcon
            title="Otáčet proti směru hodinových ručiček"
            onClick={rotateCounterClockwise}
            className={styles.rotateIcon}
            style={{ transform: `rotate(${rotateLeftDeg}deg)`, transition: 'transform 0.2s' }}
          />
          <PlusIcon
            title="Přiblížit"
            onClick={zoomIn}
            className={zoomInEnabled ? styles.zoomIconActive : styles.zoomIconInactive}
          />
          <MinusIcon
            title="Oddálit"
            onClick={zoomOut}
            className={zoomOutEnabled ? styles.zoomIconActive : styles.zoomIconInactive}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <ShareIcon
            title="Sdílet"
            onClick={handleShare}
            className={doc ? styles.actionIconActive : styles.actionIconInactive}
          />
          <DownloadIcon
            title="Stáhnout"
            onClick={handleDownload}
            className={file ? styles.actionIconActive : styles.actionIconInactive}
          />
          <PrintIcon
            title="Tisk"
            onClick={handlePrint}
            className={file ? styles.actionIconActive : styles.actionIconInactive}
          />
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            disabled={uploadDisabled}
            style={{ display: 'none' }}
          />
          <UploadIcon onClick={() => fileInputRef.current.click()} title="Nahrát přílohu" className={styles.uploadIconActive} />
        </div>
      </div>

      {viewOpen && <FilePopup pdfUrl={file} />}
    </div>
  );
};